// Orchestrates the browser-side WebRTC handshake:
//
//   1. Open the signaling WS, send `auth`, wait for `auth_ack`.
//   2. Receive the box's SDP offer + dtls_fp_sig from coord.
//   3. Extract the fingerprint from the SDP, verify the signature
//      against the expected `peer_id` ().
//   4. Only if the signature verifies: setRemoteDescription, create
//      answer, setLocalDescription, send the answer back.
//   5. Trickle ICE candidates both ways.
//   6. When the connection reaches `connected`, send `end{connected}`.
//
// The box opens the data channel ("p2claw") on its side; we just wait
// for `ondatachannel` and hand it up.

import {
  buildDtlsFpPayload,
  verifyDtlsFpSignature,
} from "./identity-verify.js";
import { extractFingerprint, fpAlgByte } from "./sdp.js";
import {
  SignalingClient,
  base64Decode,
  type RelayPayload,
  type SignalingClientOptions,
  type WebSocketFactory,
  type WebSocketLike,
} from "./signaling.js";

/** Narrow subset of RTCConfiguration we accept. Passed through unchanged. */
export interface IceConfig {
  iceServers?: RTCIceServer[];
  /**
   * Forces ICE candidate selection. `"relay"` skips host / srflx and
   * only considers TURN — used by the force-relay test hook
   * (`?p2claw-force-relay=1`) so e2e TURN coverage is exercisable even
   * when direct connectivity would normally win. `"all"` (default)
   * tries direct first and only falls back to TURN when needed.
   */
  iceTransportPolicy?: RTCIceTransportPolicy;
}

export interface PeerConnectResult {
  /** The ready `RTCPeerConnection`. Freed on session end. */
  pc: RTCPeerConnection;
  /** The data channel opened by the box ("p2claw"). */
  dataChannel: RTCDataChannel;
}

export interface ConnectOptions {
  /** 32-byte expected Ed25519 public key. From `/v1/connect` + meta-tag. */
  peerIdBytes: Uint8Array;
  /** Opaque session identifier minted by coord. */
  sessionId: string;
  /** From the `/v1/connect` response. */
  signalUrl: string;
  /** From the `/v1/connect` response. */
  signalToken: string;
  /** From the `/v1/connect` response. */
  iceConfig: IceConfig;
  /** Testing hook — tests pass a fake WS. */
  wsFactory?: WebSocketFactory;
  /** Signaling socket opened before `/v1/connect`; see `SignalingClient`. */
  preopenedSignal?: WebSocketLike;
  /**
   * Testing hook — tests inject a fake RTCPeerConnection constructor
   * (the native class is Window-only).
   */
  pcFactory?: (cfg: IceConfig) => RTCPeerConnection;
  /**
   * Fires synchronously once the fresh `RTCPeerConnection` has been
   * instantiated (before any SDP exchange). Used by the reconnect
   * loop to emit `p2claw:reconnect:pc_new` breadcrumbs so the
   * diagnostic trace distinguishes "coord responded but WebRTC
   * never got started" from "WebRTC started but DC never opened".
   */
  onPcCreated?: (pc: RTCPeerConnection) => void;
  /** Diagnostic hook for bootstrap/reconnect timing breadcrumbs. */
  onPhase?: (phase: string, detail?: string) => void;
  /** Testing hook — defaults to WebCrypto. */
  subtle?: SubtleCrypto;
  /** Abort the handshake early. */
  signal?: AbortSignal;
  /**
   * Maximum wallclock budget for the post-signaling phase of the
   * handshake — `waitForConnected` + datachannel-open. If the budget
   * elapses without the DC reaching `open`, the handshake rejects
   * with [`WebrtcHandshakeTimeoutError`].
   *
   * Catches the silent-hang failure mode (Firefox 106 with stuck
   * SCTP, blocked TURN, NAT-incompat candidate pair) where the
   * RTCPeerConnection state events never transition to `failed` —
   * users would otherwise see "connecting…" indefinitely.
   *
   * Defaults to [`DEFAULT_HANDSHAKE_TIMEOUT_MS`]. Set to `0` (or any
   * non-positive number) to disable; tests use small finite values
   * to assert the timeout fires.
   */
  handshakeTimeoutMs?: number;
}

/**
 * Default budget for the post-signaling handshake phase
 * (`waitForConnected` + DC-open). 30s mirrors the
 * Google webrtc-samples / MDN "give-up" recommendation and is large
 * enough that a slow but legitimate srflx/relay path completes within
 * it (typical: 1–5s; outliers up to ~10s on cold dial through
 * congested CGNAT).
 */
export const DEFAULT_HANDSHAKE_TIMEOUT_MS = 30_000;

export class DtlsFingerprintMismatchError extends Error {
  override name = "DtlsFingerprintMismatchError";
  constructor(detail: string) {
    super(`DTLS fingerprint signature verification failed: ${detail}`);
  }
}

/**
 * Thrown when the post-signaling handshake (`waitForConnected` →
 * DC-open) doesn't complete within `handshakeTimeoutMs`. Distinct
 * from `DtlsFingerprintMismatchError` (signature mismatch / MITM)
 * and from the abort-signal "connectToBox: aborted" error (caller
 * cancellation): this is the silent-hang case where the WebRTC
 * stack neither succeeded nor explicitly failed.
 *
 * The `lastPhase` field captures whichever phase fired most
 * recently before the timeout (`signaling_open`, `rtc_connected`,
 * `data_channel_seen`, etc.) — useful for narrowing diagnostics.
 */
export class WebrtcHandshakeTimeoutError extends Error {
  override name = "WebrtcHandshakeTimeoutError";
  /** Most-recent `onPhase` value before the timer fired. */
  public readonly lastPhase: string | null;
  constructor(timeoutMs: number, lastPhase: string | null) {
    const phaseSuffix = lastPhase
      ? ` (last phase: ${lastPhase})`
      : "";
    super(
      `WebRTC handshake did not complete within ${timeoutMs}ms${phaseSuffix}`,
    );
    this.lastPhase = lastPhase;
  }
}

export class WebcryptoUnsupportedError extends Error {
  override name = "WebcryptoUnsupportedError";
  constructor() {
    // Currently: only thrown when BOTH native WebCrypto Ed25519 AND
    // the noble fallback failed. Truly old browsers only — the
    // Firefox-106-class case (no native Ed25519, but dynamic import
    // works fine) is now handled by the noble fallback inside
    // `verifyDtlsFpSignature` and never reaches this path.
    super(
      "Your browser is too old to verify the connection's identity, and the compatibility fallback didn't load. Try Chrome 113+, Firefox 129+, or Safari 17+.",
    );
  }
}

/**
 * Run the full handshake. Resolves with a ready `RTCPeerConnection`
 * + the box-opened data channel. Rejects on fingerprint mismatch,
 * signaling timeout, or ICE failure.
 *
 * Data-channel ownership: the **box** is the offerer (
 * §7.1), so the DC is created on the box side and carried in the SDP
 * offer. The browser receives it via `ondatachannel`.
 *
 * **Ed25519 support.** No upfront probe: if the browser
 * lacks native WebCrypto Ed25519, `verifyDtlsFpSignature` falls back
 * to `@noble/ed25519` (chunk pre-warmed by the alias-binding verify
 * at boot). Only if both native and noble fail does the handshake
 * surface `WebcryptoUnsupportedError`, and only then.
 */
export async function connectToBox(
  opts: ConnectOptions,
): Promise<PeerConnectResult> {
  const pc = (opts.pcFactory ?? defaultPcFactory)(opts.iceConfig);
  opts.onPcCreated?.(pc);

  // track the most-recent phase so a backstop-timer rejection
  // can identify which step we hung on. Also fire the existing
  // onPhase callback so consumers see the same trace as before.
  let lastPhase: string | null = null;
  const onPhase = (phase: string, detail?: string): void => {
    lastPhase = phase;
    opts.onPhase?.(phase, detail);
  };
  onPhase("pc_new");

  // Capture the box-created data channel. We set this up *before* any
  // SDP exchange so there's no window where the channel fires and
  // nobody's listening.
  const dcPromise = waitForDataChannel(pc, opts.signal, onPhase);

  const signaling = new SignalingClient({
    signalUrl: opts.signalUrl,
    signalToken: opts.signalToken,
    wsFactory: opts.wsFactory,
    sessionId: opts.sessionId,
    preopened: opts.preopenedSignal,
    handlers: {
      onRelayPayload: async (payload) => {
        await handleIncoming(payload, pc, opts, signaling, onPhase);
      },
      onAuthAck: (heartbeatSec) => {
        onPhase("signaling_auth_ack", `heartbeat=${heartbeatSec}s`);
      },
      onError: (code, message) => {
        // Surface upward by closing the pc; the `connectionstatechange`
        // listener below will notice.
        console.error(`[p2claw:signaling] error ${code}: ${message}`);
        pc.close();
      },
    },
  });

  // Forward our local ICE candidates onto the relay.
  pc.onicecandidate = (ev) => {
    if (!ev.candidate) return;
    signaling.sendRelay({
      kind: "candidate",
      candidate: {
        candidate: ev.candidate.candidate,
        sdpMid: ev.candidate.sdpMid,
        sdpMLineIndex: ev.candidate.sdpMLineIndex,
      },
    });
  };

  onPhase("signaling_open_start");
  await signaling.open(opts.signal);
  onPhase("signaling_open");

  // guard the post-signaling handshake (rtc-connected → DC
  // materialises → DC reaches "open") with a wallclock backstop. If
  // the WebRTC stack hangs without firing `connectionState=failed`
  // (Firefox 106 with stuck SCTP, NAT-incompatible candidate pair,
  // blocked TURN), this rejects with `WebrtcHandshakeTimeoutError`
  // and the wrapper renders an actionable error instead of an
  // indefinite "connecting…".
  //
  // The race wraps the *handshake awaits only*. A non-positive
  // budget disables the timer (test seam).
  const handshakeTimeoutMs =
    opts.handshakeTimeoutMs ?? DEFAULT_HANDSHAKE_TIMEOUT_MS;
  const handshakeAwaits = (async () => {
    // Wait for (a) the connection to be established, (b) the DC to
    // materialise via `ondatachannel`, (c) the DC to open.
    await waitForConnected(pc, opts.signal, onPhase);
    const dc = await dcPromise;
    await waitForDataChannelOpen(dc, opts.signal, onPhase);
    return dc;
  })();
  const dc = await raceWithHandshakeTimeout(
    handshakeAwaits,
    handshakeTimeoutMs,
    () => lastPhase,
  );

  signaling.end("connected");
  signaling.close();

  // One-shot diagnostic log for whether the ICE path is host / srflx /
  // relay — Playwright reads this to verify TURN coverage when the
  // force-relay hook is active. Failures are swallowed (older runtimes
  // and test fakes without `getStats` must not crash connect).
  void logIcePath(pc);

  // One-shot log of the SCTP transport's negotiated
  // `maxMessageSize`. iOS Safari has historically negotiated
  // smaller SCTP message sizes than Chrome/Firefox; a value smaller
  // than the box-side outbound frame size silently drops receives
  // on the DC, so the value is worth recording per session. Failures
  // swallowed for the same reason as `logIcePath`.
  logSctpInfo(pc, dc);

  return { pc, dataChannel: dc };
}

/**
 * Log `p2claw:ice-path <candidateType>` once, using the currently
 * selected/nominated candidate pair from `getStats()`. Intended as a
 * hook for e2e tests (force-relay) to assert which ICE path won;
 * production just emits an `info`-level breadcrumb. Exported so unit
 * tests can drive it with a fake `RTCPeerConnection` stub without
 * having to stand up the full signaling handshake.
 */
export async function logIcePath(pc: RTCPeerConnection): Promise<void> {
  try {
    if (typeof pc.getStats !== "function") return;
    // `RTCStatsReport` iterates like a `Map<string, any>` in every
    // browser that implements it — the `values()` / `get()` methods
    // are inherited from the underlying Map. TS's `lib.dom.d.ts`
    // declares them under a stricter surface, so cast to the
    // iterable/gettable shape we actually use.
    const stats = (await pc.getStats()) as unknown as Map<string, unknown>;
    for (const report of stats.values()) {
      const r = report as {
        type?: string;
        nominated?: boolean;
        state?: string;
        localCandidateId?: string;
      };
      if (
        r.type === "candidate-pair" &&
        r.nominated === true &&
        r.state === "succeeded" &&
        typeof r.localCandidateId === "string"
      ) {
        const local = stats.get(r.localCandidateId) as
          | { candidateType?: string }
          | undefined;
        const candidateType = local?.candidateType ?? "unknown";
        console.info("p2claw:ice-path", candidateType);
        return;
      }
    }
  } catch {
    /* swallow — diagnostic only */
  }
}

/**
 * Snapshot of WebRTC stats relevant to diagnosing missing iframe
 * bodies. Populated from `pc.getStats(dc)` at stall-watchdog time
 * so a trace shows whether bytes left the box, reached the peer's
 * SCTP, and/or reached the data channel — even when the SW-level
 * `onmessage` never fired.
 *
 * All fields are optional because not every user agent populates
 * the full set; missing counters report as `undefined` rather than
 * absent keys so JSON.stringify yields a stable shape across
 * browsers.
 */
export interface DcStatsSummary {
  /** Per-`data-channel` counters from `getStats()`. */
  dc: {
    state?: string;
    messagesReceived?: number;
    bytesReceived?: number;
    messagesSent?: number;
    bytesSent?: number;
    label?: string;
    protocol?: string;
  };
  /** `sctp-transport` counters (W3C `RTCSctpTransportStats`). */
  sctp: {
    state?: string;
    bytesReceived?: number;
    bytesSent?: number;
    smoothedRoundTripTime?: number;
  };
  /** `transport` counters (DTLS + ICE wrapper). */
  transport: {
    bytesReceived?: number;
    bytesSent?: number;
    dtlsState?: string;
    iceState?: string;
    selectedCandidatePairId?: string;
  };
  /** Nominated candidate pair's wire counters — disambiguates
   *  "no DTLS bytes" from "no ICE bytes". */
  candidatePair: {
    bytesReceived?: number;
    bytesSent?: number;
    packetsReceived?: number;
    packetsSent?: number;
    requestsReceived?: number;
    responsesReceived?: number;
    state?: string;
    nominated?: boolean;
  };
}

const EMPTY_DC_STATS: DcStatsSummary = {
  dc: {},
  sctp: {},
  transport: {},
  candidatePair: {},
};

/**
 * Pull a `DcStatsSummary` from `pc.getStats(dc)`. Used by
 * the SW handoff's stall watchdog to print "what the WebRTC stack
 * actually saw" when the body pump gave up.
 *
 * Spec ambiguity workaround: `getStats(dc)` is supposed to filter
 * to data-channel-related reports, but support varies. We pass `dc`
 * when callers provide it and still iterate the full report so
 * sctp-transport / transport / candidate-pair entries land too.
 * Throws are swallowed (returns `EMPTY_DC_STATS`) because this is
 * pure diagnostic — must not destabilise an already-failing path.
 *
 * Exported for unit tests.
 */
export async function collectDcStats(
  pc: RTCPeerConnection,
  dc?: RTCDataChannel,
): Promise<DcStatsSummary> {
  try {
    if (typeof pc.getStats !== "function") return EMPTY_DC_STATS;
    // TS's lib.dom.d.ts types `getStats` as taking `MediaStreamTrack |
    // null`, even though the W3C-RTC spec also allows a data channel
    // as the selector argument. Some user agents do honour the DC
    // selector and trim the report; others ignore it and return the
    // full set. We rely on filtering by `type` below regardless, so
    // it's safe to call with no selector — the DC param is kept for
    // future use (when the lib types are updated) and surfaced in
    // the function signature for documentation purposes.
    void dc;
    const report = (await pc.getStats()) as unknown as Map<string, unknown>;
    const out: DcStatsSummary = {
      dc: {},
      sctp: {},
      transport: {},
      candidatePair: {},
    };
    let selectedPairId: string | undefined;
    let dcFallback: Record<string, unknown> | null = null;
    for (const [, raw] of report) {
      const r = raw as Record<string, unknown> & { type?: string };
      switch (r.type) {
        case "data-channel": {
          // If multiple channels are reported, prefer the one whose
          // label matches `dc.label`; fall back to the first.
          if (
            dc &&
            typeof r["label"] === "string" &&
            r["label"] !== dc.label
          ) {
            if (dcFallback === null) dcFallback = r;
            break;
          }
          assignDcFields(out.dc, r);
          break;
        }
        case "sctp-transport": {
          if (typeof r["state"] === "string") out.sctp.state = r["state"] as string;
          if (typeof r["bytesReceived"] === "number") out.sctp.bytesReceived = r["bytesReceived"] as number;
          if (typeof r["bytesSent"] === "number") out.sctp.bytesSent = r["bytesSent"] as number;
          if (typeof r["smoothedRoundTripTime"] === "number") {
            out.sctp.smoothedRoundTripTime = r["smoothedRoundTripTime"] as number;
          }
          break;
        }
        case "transport": {
          if (typeof r["bytesReceived"] === "number") out.transport.bytesReceived = r["bytesReceived"] as number;
          if (typeof r["bytesSent"] === "number") out.transport.bytesSent = r["bytesSent"] as number;
          if (typeof r["dtlsState"] === "string") out.transport.dtlsState = r["dtlsState"] as string;
          if (typeof r["iceState"] === "string") out.transport.iceState = r["iceState"] as string;
          if (typeof r["selectedCandidatePairId"] === "string") {
            selectedPairId = r["selectedCandidatePairId"] as string;
            out.transport.selectedCandidatePairId = selectedPairId;
          }
          break;
        }
        default:
          break;
      }
    }
    // If we never matched the dc by label, take the fallback.
    if (Object.keys(out.dc).length === 0 && dcFallback !== null) {
      assignDcFields(out.dc, dcFallback);
    }
    // Resolve the candidate-pair counters now that we know which id
    // is selected (or via the nominated/state heuristic if `transport`
    // didn't carry `selectedCandidatePairId`).
    let chosenPair: Record<string, unknown> | null = null;
    if (selectedPairId !== undefined) {
      chosenPair = (report.get(selectedPairId) as Record<string, unknown>) ?? null;
    }
    if (chosenPair === null) {
      for (const [, raw] of report) {
        const r = raw as Record<string, unknown> & { type?: string };
        if (
          r.type === "candidate-pair" &&
          r["nominated"] === true &&
          r["state"] === "succeeded"
        ) {
          chosenPair = r;
          break;
        }
      }
    }
    if (chosenPair !== null) {
      const cp = chosenPair;
      if (typeof cp["bytesReceived"] === "number") out.candidatePair.bytesReceived = cp["bytesReceived"] as number;
      if (typeof cp["bytesSent"] === "number") out.candidatePair.bytesSent = cp["bytesSent"] as number;
      if (typeof cp["packetsReceived"] === "number") out.candidatePair.packetsReceived = cp["packetsReceived"] as number;
      if (typeof cp["packetsSent"] === "number") out.candidatePair.packetsSent = cp["packetsSent"] as number;
      if (typeof cp["requestsReceived"] === "number") out.candidatePair.requestsReceived = cp["requestsReceived"] as number;
      if (typeof cp["responsesReceived"] === "number") out.candidatePair.responsesReceived = cp["responsesReceived"] as number;
      if (typeof cp["state"] === "string") out.candidatePair.state = cp["state"] as string;
      if (typeof cp["nominated"] === "boolean") out.candidatePair.nominated = cp["nominated"] as boolean;
    }
    return out;
  } catch {
    return EMPTY_DC_STATS;
  }
}

function assignDcFields(
  out: DcStatsSummary["dc"],
  r: Record<string, unknown>,
): void {
  if (typeof r["state"] === "string") out.state = r["state"] as string;
  if (typeof r["messagesReceived"] === "number") out.messagesReceived = r["messagesReceived"] as number;
  if (typeof r["bytesReceived"] === "number") out.bytesReceived = r["bytesReceived"] as number;
  if (typeof r["messagesSent"] === "number") out.messagesSent = r["messagesSent"] as number;
  if (typeof r["bytesSent"] === "number") out.bytesSent = r["bytesSent"] as number;
  if (typeof r["label"] === "string") out.label = r["label"] as string;
  if (typeof r["protocol"] === "string") out.protocol = r["protocol"] as string;
}

/**
 * Log `p2claw:sctp <maxMessageSize=… state=… maxChannels=…>` once
 * after the data channel opens. The `RTCSctpTransport.
 * maxMessageSize` is the negotiated peer-level upper bound on a
 * single SCTP message; bytes beyond this on the receive side are
 * silently dropped by some user agents (notably iOS Safari).
 * Surfacing it next to `data_channel_open` lets a paired Web
 * Inspector trace compare cross-device budgets and decide whether
 * to chunk outbound frames more aggressively on the box side.
 *
 * Exported so unit tests can drive it with a fake `RTCPeerConnection`.
 * Synchronous helper because the values are present immediately on
 * `pc.sctp` after the DC opens; no `getStats()` round-trip needed.
 */
export function logSctpInfo(
  pc: RTCPeerConnection,
  dc: RTCDataChannel,
): void {
  try {
    // `RTCSctpTransport` is the standard surface for these fields;
    // `RTCDataChannel.maxMessageSize` is non-standard but exposed by
    // some user agents — read it as a tie-breaker if the SCTP
    // transport is unavailable.
    const sctp = (pc as { sctp?: RTCSctpTransport | null }).sctp ?? null;
    const dcMax = (dc as unknown as { maxMessageSize?: number }).maxMessageSize;
    const fields: string[] = [];
    if (sctp) {
      fields.push(`sctp.maxMessageSize=${sctp.maxMessageSize}`);
      fields.push(`sctp.state=${sctp.state}`);
      if (typeof sctp.maxChannels === "number") {
        fields.push(`sctp.maxChannels=${sctp.maxChannels}`);
      }
    } else {
      fields.push("sctp=null");
    }
    if (typeof dcMax === "number") {
      fields.push(`dc.maxMessageSize=${dcMax}`);
    }
    fields.push(`dc.bufferedAmountLowThreshold=${dc.bufferedAmountLowThreshold}`);
    console.info(`p2claw:sctp ${fields.join(" ")}`);
  } catch {
    /* swallow — diagnostic only */
  }
}

// ---------- incoming relay dispatcher ----------

async function handleIncoming(
  payload: RelayPayload,
  pc: RTCPeerConnection,
  opts: ConnectOptions,
  signaling: SignalingClient,
  onPhase: (phase: string, detail?: string) => void,
): Promise<void> {
  switch (payload.kind) {
    case "offer": {
      if (!payload.sdp) throw new Error("offer: missing sdp");
      onPhase("offer_received");
      await verifyFingerprintOrThrow(payload, opts);
      onPhase("dtls_verified");
      await pc.setRemoteDescription({ type: "offer", sdp: payload.sdp });
      onPhase("remote_description_set");
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      signaling.sendRelay({ kind: "answer", sdp: answer.sdp ?? "" });
      onPhase("answer_sent");
      break;
    }
    case "answer": {
      if (!payload.sdp) throw new Error("answer: missing sdp");
      // Per  the box sends the offer, not the
      // answer. Kept here for symmetry / future protocol revisions.
      await verifyFingerprintOrThrow(payload, opts);
      await pc.setRemoteDescription({ type: "answer", sdp: payload.sdp });
      break;
    }
    case "candidate": {
      if (!payload.candidate) return;
      await pc.addIceCandidate({
        candidate: payload.candidate.candidate,
        sdpMid: payload.candidate.sdpMid ?? undefined,
        sdpMLineIndex: payload.candidate.sdpMLineIndex ?? undefined,
      });
      break;
    }
  }
}

async function verifyFingerprintOrThrow(
  payload: RelayPayload,
  opts: ConnectOptions,
): Promise<void> {
  const sigB64 = payload.dtls_fp_sig_b64;
  if (!sigB64) {
    throw new DtlsFingerprintMismatchError(
      "box did not include dtls_fp_sig_b64 on its initial SDP — refusing to connect",
    );
  }
  const sdp = payload.sdp ?? "";
  const fp = extractFingerprint(sdp);
  if (!fp) {
    throw new DtlsFingerprintMismatchError(
      "no a=fingerprint line in box SDP — refusing to connect",
    );
  }
  const signature = base64Decode(sigB64);
  const result = await verifyDtlsFpSignature({
    peerIdBytes: opts.peerIdBytes,
    sessionId: opts.sessionId,
    fpAlg: fpAlgByte(fp.algorithm),
    fp: fp.value,
    signature,
    subtle: opts.subtle,
  });
  if (result.ok) return;

  // Tagged result lets us distinguish:
  //   `unsupported` — neither native WebCrypto Ed25519 nor the noble
  //                   fallback could run. Browser is genuinely too
  //                   old. Surface the upgrade-hint copy, NOT the
  //                   MITM copy. Same shape as wrapper.ts uses for
  //                   `verifyAliasBinding`'s unsupported branch.
  //   `invalid`     — at least one verifier ran end-to-end and
  //                   rejected. Real signature mismatch — possible
  //                   MITM, surface the existing scary copy with
  //                   payload-length context for operator diff.
  if (result.reason === "unsupported") {
    throw new WebcryptoUnsupportedError();
  }
  // Surface the exact bytes we computed so an operator can diff
  // against the box's log. Never log the signature itself.
  const payloadBytes = buildDtlsFpPayload({
    peerIdBytes: opts.peerIdBytes,
    sessionId: opts.sessionId,
    fpAlg: fpAlgByte(fp.algorithm),
    fp: fp.value,
  });
  throw new DtlsFingerprintMismatchError(
    `signature did not verify over ${payloadBytes.length}-byte payload`,
  );
}

// ---------- connection-state plumbing ----------

/**
 * race a handshake-Promise against a wallclock backstop. On
 * timeout, rejects with [`WebrtcHandshakeTimeoutError`] carrying the
 * most-recent phase (read at fire time, not capture time) so the
 * error surface tells the operator which step hung.
 *
 * Non-positive `timeoutMs` skips the race entirely — used by tests
 * that want to assert non-timeout behaviors deterministically.
 *
 * Exported for unit tests only — production callers reach it via
 * `connectToBox`.
 */
export function raceWithHandshakeTimeout<T>(
  inner: Promise<T>,
  timeoutMs: number,
  readLastPhase: () => string | null,
): Promise<T> {
  if (timeoutMs <= 0) return inner;
  let timerId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timerId = setTimeout(() => {
      reject(new WebrtcHandshakeTimeoutError(timeoutMs, readLastPhase()));
    }, timeoutMs);
  });
  return Promise.race([inner, timeout]).finally(() => {
    if (timerId !== undefined) clearTimeout(timerId);
  });
}

async function waitForConnected(
  pc: RTCPeerConnection,
  signal?: AbortSignal,
  onPhase?: (phase: string, detail?: string) => void,
): Promise<void> {
  if (pc.connectionState === "connected") {
    onPhase?.("rtc_connected");
    return;
  }
  await new Promise<void>((resolve, reject) => {
    const onChange = () => {
      switch (pc.connectionState) {
        case "connected":
          cleanup();
          onPhase?.("rtc_connected");
          resolve();
          break;
        case "failed":
          cleanup();
          reject(new Error("RTCPeerConnection: state=failed"));
          break;
        case "closed":
          cleanup();
          reject(new Error("RTCPeerConnection: state=closed before connected"));
          break;
      }
    };
    // older browsers (notably Firefox < 113) don't compute
    // the aggregate `connectionState` reliably; `iceConnectionState`
    // is the more authoritative ICE-layer signal. Listen to both:
    // `iceConnectionState=failed` rejects faster than waiting for
    // `connectionState=failed` to (eventually) catch up, AND covers
    // the browsers where the aggregate never fires at all.
    const onIceChange = () => {
      if (pc.iceConnectionState === "failed") {
        cleanup();
        reject(new Error("RTCPeerConnection: iceConnectionState=failed"));
      }
    };
    const onAbort = () => {
      cleanup();
      reject(new Error("connectToBox: aborted"));
    };
    const cleanup = () => {
      pc.removeEventListener("connectionstatechange", onChange);
      pc.removeEventListener("iceconnectionstatechange", onIceChange);
      signal?.removeEventListener("abort", onAbort);
    };
    pc.addEventListener("connectionstatechange", onChange);
    pc.addEventListener("iceconnectionstatechange", onIceChange);
    signal?.addEventListener("abort", onAbort);
  });
}

async function waitForDataChannel(
  pc: RTCPeerConnection,
  signal?: AbortSignal,
  onPhase?: (phase: string, detail?: string) => void,
): Promise<RTCDataChannel> {
  return new Promise<RTCDataChannel>((resolve, reject) => {
    const onDc = (ev: RTCDataChannelEvent) => {
      if (ev.channel.label !== "p2claw") return;
      cleanup();
      onPhase?.("data_channel_seen", `label=${ev.channel.label}`);
      resolve(ev.channel);
    };
    const onAbort = () => {
      cleanup();
      reject(new Error("connectToBox: aborted before datachannel"));
    };
    const cleanup = () => {
      pc.removeEventListener("datachannel", onDc);
      signal?.removeEventListener("abort", onAbort);
    };
    pc.addEventListener("datachannel", onDc);
    signal?.addEventListener("abort", onAbort);
  });
}

async function waitForDataChannelOpen(
  dc: RTCDataChannel,
  signal?: AbortSignal,
  onPhase?: (phase: string, detail?: string) => void,
): Promise<void> {
  if (dc.readyState === "open") {
    onPhase?.("data_channel_open");
    return;
  }
  await new Promise<void>((resolve, reject) => {
    const onOpen = () => {
      cleanup();
      onPhase?.("data_channel_open");
      resolve();
    };
    const onClose = () => {
      cleanup();
      reject(new Error("DataChannel: closed before open"));
    };
    const onError = () => {
      cleanup();
      reject(new Error("DataChannel: error before open"));
    };
    const onAbort = () => {
      cleanup();
      reject(new Error("connectToBox: aborted"));
    };
    const cleanup = () => {
      dc.removeEventListener("open", onOpen);
      dc.removeEventListener("close", onClose);
      dc.removeEventListener("error", onError);
      signal?.removeEventListener("abort", onAbort);
    };
    dc.addEventListener("open", onOpen);
    dc.addEventListener("close", onClose);
    dc.addEventListener("error", onError);
    signal?.addEventListener("abort", onAbort);
  });
}

// ---------- default RTCPeerConnection factory ----------

const defaultPcFactory = (cfg: IceConfig): RTCPeerConnection => {
  const ctor = (
    globalThis as { RTCPeerConnection?: typeof RTCPeerConnection }
  ).RTCPeerConnection;
  if (!ctor) {
    throw new Error(
      "no RTCPeerConnection available (browser only; SW and worker contexts are unsupported)",
    );
  }
  return new ctor(cfg);
};

/** Forwarded from signaling.ts so callers only import from `connection.ts`. */
export type { SignalingClientOptions };
